import React, { useCallback, useMemo } from 'react';
import { TreeView } from '@material-ui/lab';
import { TreeNode, ITreeNodeProps, getNodePosition, ITreeNode } from './TreeNode';
import { SkeletonTree } from 'loaders/SkeletonTree';
import classNames from 'classnames';
import './Tree.less';
import { findNodesList } from '../Legacy/useTreeModel';

export interface ITreeProps<T> {
  treeId?: string;
  /**
   * for root tree element
   */
  treeClassName?: string;
  /**
   * For node styles (for example: add padding)
   */
  nodeClassName?: string;
  nodes: ITreeNode<T>[];
  getTreeNode?: (node: ITreeNodeProps<T>) => React.ReactNode;
  onNodeKeyDown?: (node: ITreeNode<T>, e: React.KeyboardEvent) => void;
  onNodeExpand?: (node: ITreeNode<T>) => void;
  onNodeFold?: (node: ITreeNode<T>) => void;
  onNodeSelect?: (node: ITreeNode<T>) => void;
  loading?: {
    nodeLoadingType?: NodeLoadingType
    isLoading: boolean;
    height: number;
  };
}

export enum NodeLoadingType {
  Spinner,
  Skeleton
}

function getTreeClassNames(additionalClassName?: string) {
  return classNames('dpc-tree', additionalClassName);
}

/**
 * @deprecated Please use /CommonTree/Tree.tsx
 */
export function Tree<T>({
  treeId,
  treeClassName,
  nodes,
  getTreeNode,
  nodeClassName,
  onNodeKeyDown,
  onNodeExpand,
  onNodeFold,
  onNodeSelect,
  loading,
  ...props
}: ITreeProps<T>) {
  const getNodeProps = useCallback(
    (node: ITreeNode<T>) => {
      return {
        ...node,
        onNodeKeyDown,
        getTreeNode,
        onNodeExpand,
        onNodeFold,
        onNodeSelect,
        baseClassName: nodeClassName,
      };
    },
    [onNodeKeyDown, getTreeNode, onNodeExpand, onNodeFold, onNodeSelect, nodeClassName]
  );

  const expandedNodes: string[] = useMemo(() => {
    return findNodesList(node => !!node.isExpanded, nodes).map(n => n.nodeId);
  }, [nodes]);

  if (loading?.isLoading) {
    return <SkeletonTree height={loading.height} />;
  }

  return (
    <TreeView expanded={expandedNodes} id={treeId!} className={getTreeClassNames(treeClassName)} {...props}>
      {nodes.map((node: ITreeNode<T>, i, arr) => {
        return <TreeNode key={node.nodeId} nodeLoadingType={loading?.nodeLoadingType} nodePosition={getNodePosition(i, arr.length)} {...getNodeProps(node)} />;
      })}
    </TreeView>
  );
}
